import { datadogRum } from '@datadog/browser-rum';
import { reactPlugin } from '@datadog/browser-rum-react';

import config from '@@config';

const STACK_FRAME_PATTERN = /^\s*(?:at\b|.*@[a-z][\w+.-]*:\/\/|[a-z][\w+.-]*:\/\/)/i;
const BROWSER_EXTENSION_URL_PATTERN =
    /\b(?:chrome-extension|moz-extension|safari-web-extension):\/\//i;
const STACK_FRAME_URL_PATTERN = /\b[a-z][\w+.-]*:\/\/[^\s)]+/gi;
const DATADOG_BUNDLE_URL_PATTERN = /\/assets\/datadog-[^/\s]+\.js(?::\d+:\d+)?$/i;

const isStagingOrProd =
    config.env === 'staging' || config.env === 'production' || config.env === 'prod';

export const isDataDogEnabled = () =>
    config.dataDogApplicationId &&
    config.dataDogClientToken &&
    isStagingOrProd &&
    config.mode !== 'test' &&
    !config.isVisualTest;

type RumEventForFiltering = {
    type: string;
    error?: {
        message?: string;
        stack?: string;
    };
};

export const shouldSendDataDogRumEvent = (event: RumEventForFiltering) => {
    if (event.type !== 'error' || !event.error?.stack) {
        return true;
    }

    const firstStackFrame = event.error.stack
        .split('\n')
        .find((line) => STACK_FRAME_PATTERN.test(line));

    if (firstStackFrame && BROWSER_EXTENSION_URL_PATTERN.test(firstStackFrame)) {
        return false;
    }

    if (event.error.message !== 'Failed to fetch') {
        return true;
    }

    const stackFrameUrls = event.error.stack.match(STACK_FRAME_URL_PATTERN) ?? [];

    return (
        stackFrameUrls.length === 0 ||
        stackFrameUrls.some(
            (url) =>
                !BROWSER_EXTENSION_URL_PATTERN.test(url) && !DATADOG_BUNDLE_URL_PATTERN.test(url),
        )
    );
};

export const initializeDataDogRUM = () => {
    if (isDataDogEnabled()) {
        datadogRum.init({
            applicationId: config.dataDogApplicationId,
            clientToken: config.dataDogClientToken,
            env: config.env,
            site: 'datadoghq.eu',
            // Specify a version number to identify the deployed version of your application in Datadog
            version: window.VERSION ?? undefined,
            sessionSampleRate: 100,
            sessionReplaySampleRate: 100,
            // disable telemetry
            telemetrySampleRate: 0,
            trackUserInteractions: true,
            trackResources: true,
            trackLongTasks: true,
            defaultPrivacyLevel: 'mask-user-input',
            enablePrivacyForActionName: false,
            // percent of server traces sampled
            traceSampleRate: 100,
            service: 'unitycms',
            // https://docs.datadoghq.com/real_user_monitoring/connect_rum_and_traces/?tab=browserrum
            allowedTracingUrls: [
                { match: /https:\/\/.*\.unitycms\.io/, propagatorTypes: ['b3multi'] },
            ],
            beforeSend: shouldSendDataDogRumEvent,
            // Views are named after their route by the router integration in `@@router/router`
            plugins: [reactPlugin({ router: true })],
        });
    }

    datadogRum.startSessionReplayRecording();
};

export const setDataDogRUMUserId = (id: string) => {
    if (isDataDogEnabled()) {
        datadogRum.setUser({
            id,
        });
    }
};

export const addDataDogRUMAction = (name: string, context: Record<string, string | boolean>) => {
    if (isDataDogEnabled()) {
        datadogRum.addAction(name, context);
    }
};

export const addDatadogError = (error: unknown, context?: UnknownObject) => {
    if (isDataDogEnabled()) {
        datadogRum.addError(error, context);
    }
};
